import {
  AfterViewInit,
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';

import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { map, finalize, take } from 'rxjs';

import { FormsService } from 'src/app/core/services/forms.service';
import { CompanyService } from 'src/app/core/services/company.service';
import {
  ImportUsersResult,
  UserService,
} from 'src/app/core/services/user.service';

import {
  DialogCloseReason,
  DialogCloseOptions,
  injectDirtyCloseHandler,
} from '@sui/dialog';

import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';
import { FormProperty } from 'src/app/core/models/form.models';
import { ToastService } from '@services/toast.service';

interface DialogData {}
interface DialogOutput {}

@Component({
  selector: 'app-import-users-dialog',
  templateUrl: './import-users-dialog.component.html',
  styleUrl: './import-users-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImportUsersDialogComponent
  extends SimpleDialogBase<DialogData, DialogOutput>
  implements AfterViewInit
{
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  protected formService = inject(FormsService);
  protected companyService = inject(CompanyService);
  protected userService = inject(UserService);
  protected toast = inject(ToastService);

  actionIcon = faUserPlus;
  dialogTitle = this.translateService.instant(
    'import-users-popup.title.import-users',
  );

  fileName: string = 'import-users-template.xlsx';
  templateUrl: string = 'assets/' + this.fileName;
  searchString: string = '';

  isSaving = false;
  backendError: any = null;

  fileMissingError = false;

  public formProperties: FormProperty[] = [
    {
      propertyName: 'file-input',
      controlType: 'file-input',
      propertyLabel: this.translateService.instant(
        'import-users-popup.label.choose-a-csv-or-xlsx-file',
      ),
      validators: [Validators.required],
      additionalConfig: {
        types: '.csv, .xlsx',
      },
    },
    {
      propertyName: 'company',
      controlType: 'select',
      propertyLabel: this.translateService.instant(
        'import-users-popup.label.choose-company',
      ),
      validators: [Validators.required],
      valueOptionsAsync: this.companyService.fetchCompanies().pipe(
        map((companies) =>
          companies.map((company) => ({
            value: company.id,
            name: company.name,
          })),
        ),
      ),
    },
    {
      propertyName: 'invite',
      controlType: 'checkbox',
      propertyLabel: this.translateService.instant(
        'import-users-popup.label.invite-new-users',
      ),
    },
  ];

  protected formGroup = this.formService.createFormGroup(this.formProperties);

  private formValueChanges = toSignal(this.formGroup.valueChanges, {
    requireSync: false,
  });

  private formStatusChanges = toSignal(this.formGroup.statusChanges, {
    initialValue: this.formGroup.status,
  });

  // The selected file now lives in the 'file-input' form control (sui-file-input
  // is a File-valued CVA). Derive it, reacting to form value changes.
  public selectedFile = computed<File | null>(() => {
    this.formValueChanges();
    const value = this.formGroup.get('file-input')?.value;
    return value instanceof File ? value : null;
  });

  public isDirty = computed(() => {
    // make the computed react to form changes
    this.formValueChanges();
    return this.formGroup.dirty || this.selectedFile() !== null;
  });

  public isValid = computed(() => {
    const status = this.formStatusChanges();
    const hasFile = this.selectedFile() !== null;
    return status === 'VALID' && hasFile;
  });

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    this.backendError = null;

    const companyId = this.formGroup.get('company')?.value;
    const invite = !!this.formGroup.get('invite')?.value;
    const file = this.selectedFile();

    // mark touched so the UI shows validation messages
    this.formGroup.markAllAsTouched();

    if (!companyId) {
      this.formGroup.get('company')?.setErrors({ required: true });
      return { reason: DialogCloseReason.Cancel, data: {} as any };
    }

    if (!file) {
      this.fileMissingError = true;
      return { reason: DialogCloseReason.Cancel, data: {} as any };
    }

    // This return value is used only after the request succeeds.
    return {
      reason: DialogCloseReason.Save,
      data: {} as any,
    };
  }

  /**
   * Save click entry-point.
   * Runs the backend call and closes the dialog ONLY when successful.
   */
  save(): void {
    if (this.isSaving) return;

    const closeOptions = this.performSave();

    // If performSave decided we're not good to go, just stay open
    if (closeOptions.reason !== DialogCloseReason.Save) {
      return;
    }

    const companyId = this.formGroup.get('company')?.value as string;
    const invite = !!this.formGroup.get('invite')?.value;
    const file = this.selectedFile() as File;

    this.isSaving = true;
    this.backendError = null;

    this.userService
      .importUsers(companyId, file, invite)
      .pipe(
        take(1),
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe({
        next: (result: ImportUsersResult) => {
          this.showImportResultToast(result);

          this.formGroup.get('file-input')?.reset(null);
          this.formGroup.markAsPristine();

          this.dirtyHandler.closeImmediately({
            reason: DialogCloseReason.Save,
            data: result,
          });
        },
        error: (err) => {
          this.toast.showError(
            this.translateService.instant('import-users.toast.request-error'),
          );
          this.backendError = err;
        },
      });
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }

  private showImportResultToast(result: ImportUsersResult): void {
    const { success, successCount, failureCount } = result;

    if (success && failureCount === 0) {
      this.toast.showSuccess(
        this.translateService.instant('import-users.toast.success', {
          count: successCount,
        }),
      );
      return;
    }

    if (successCount > 0 && failureCount > 0) {
      this.toast.showWarning(
        this.translateService.instant('import-users.toast.partial', {
          successCount,
          failureCount,
        }),
      );
      return;
    }

    this.toast.showError(
      this.translateService.instant('import-users.toast.failed', {
        failureCount,
      }),
    );
  }
}
